
import { defineComponent } from 'vue'
import { mapState } from 'vuex'
import { debounce, isEmpty } from 'lodash'
import { format as dateFormat } from 'date-fns'
import NrEditor from '@nextrequestco/components/src/components/NrEditor/nr-editor.vue'
import DocumentUploadModal from '../../../vue_shared/components/DocumentUploadModal.vue'
import NrIcon from '@nextrequestco/components/src/components/NrIcon/NrIcon.vue'
import NrButton from '@nextrequestco/components/src/components/NrButton/nr-button.vue'
import NrTooltip from '@nextrequestco/components/src/components/NrTooltip/NrTooltip.vue'
import NrModal from '@nextrequestco/components/src/components/NrModal/nr-modal.vue'
import NrSelect from '@nextrequestco/components/src/components/NrSelect/nr-select.vue'
import NrFormInput from '@nextrequestco/components/src/components/NrFormInput/nr-form-input.vue'
import NrFormSelect from '@nextrequestco/components/src/components/NrFormSelect/nr-form-select'
import {
  State,
  RequestVisibilityValue,
  RequestVisibility,
  accountRequestVisibilities
} from 'src/store/globalModule'
import * as http from '../../../vue_shared/http'
import NrDatepicker from '@nextrequestco/components/src/components/NrDatePicker/nr-datepicker.vue'
import RequestFieldInput from '../../../vue_shared/components/RequestFieldInput.vue'
import {
  FieldDefinition,
  RequestFieldValue
} from '../../../vue_shared/types/request_fields'
import CustomFieldsPreviewBanner from './custom_fields_preview_banner.vue'

interface NewRequestPrep {
  states: [string, string][]
  formats: { id: number; name: string }[]
  embargo_visibility_tooltip: string | undefined // eslint-disable-line camelcase
  request_fields: FieldDefinition[] // eslint-disable-line camelcase
}

interface Document {
  id: number
  asset_url: string // eslint-disable-line camelcase
  [key: string]: unknown
}

interface Requester {
  email?: string
  name?: string
  phone?: string
  address?: string
  city?: string
  state?: string
  zip?: string
  company?: string
}

interface NewRequest {
  requester: Requester
  documents: object[]
  folder: string
  request: {
    request_text: string // eslint-disable-line camelcase
    request_date: string // eslint-disable-line camelcase
    request_submit_type_id: number // eslint-disable-line camelcase
    visibility: string
    department_ids: number[] // eslint-disable-line camelcase
  }
}

interface AccountUser extends Requester {
  active: boolean
  enabled: boolean
}

interface RequestField {
  definition: FieldDefinition
  value: RequestFieldValue | null
  // eslint-disable-next-line camelcase
  type_settings?: Record<string, string | number>
}

// Regular expression taken from https://html.spec.whatwg.org/multipage/input.html#valid-e-mail-address
const EMAIL_REGEX =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/

export default defineComponent({
  components: {
    DocumentUploadModal,
    NrEditor,
    NrIcon,
    NrModal,
    NrSelect,
    NrFormInput,
    CustomFieldsPreviewBanner,
    NrFormSelect,
    NrButton,
    NrTooltip,
    NrDatepicker,
    RequestFieldInput
  },
  props: {
    customFieldsPreview: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showNoRequesterEmailModal: false,
      isMakingRequest: false,
      invisibleCaptcha: '',
      dateReceived: dateFormat(new Date(), 'MM/dd/yyyy'),
      departmentSearchTerm: '',
      documentsToAdd: [],
      formats: [],
      formatReceived: '',
      embargoVisibilityTip: null,
      emailDisabled: false,
      emailNotice: '',
      keywordAlerts: [],
      accessibilityAlerts: [],
      requestText: '',
      isRequesterValid: {
        email: true,
        name: true,
        phone: true,
        address: true,
        city: true,
        state: true,
        zip: true
      },
      isRequestFieldsValid: {},
      requester: {
        email: '',
        name: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        zip: '',
        company: ''
      },
      selectedDepartments: [],
      selectedFolder: null,
      showUploadModal: false,
      states: [],
      termsAndConditionsChecked: false,
      uploadedDocuments: [],
      requestVisibility: null,
      isRequesterFieldsDisabled: false,
      requestFields: [],
      userTypes: ['Staff', 'Guest'],
      currentUserPerspective: 'Staff',
      selectedUserType: 'Staff',
      showPerspectiveModal: false
    }
  },

  mounted() {
    this.getNewRequestData()
    this.handleRequestInput = debounce(this.handleRequestInput, 400)
    this.parseQueryParams()
  },
  computed: {
    ...mapState({
      account: (state: State): object | null => state.account,
      currentUser: (state: State): object | null => state.currentUser,
      showDepartments(state: State): boolean {
        return (
          this.$can('read', 'staff_only_items') ||
          state.account?.show_dept_select
        )
      },
      departmentRequired: (state: State): '*' | '' =>
        state.account?.require_department ? '*' : '',
      departments: (state: State): { id: number; name: string }[] =>
        state.account?.departments || [],
      sidebarRequestText: (state: State): string =>
        state.account?.sidebar_request_text || '',
      subheader: (state: State): string =>
        state.account?.request_page_subheader || '',
      termsAndConditions: (state: State): string =>
        state.account?.terms_and_conditions || '',
      requesterRequired(state: State): '*' | null {
        if (this.isRequesterFieldsDisabled) {
          return null
        } else {
          return (
            (state.account?.required_fields &&
              !this.$can('read', 'staff_only_items') &&
              '*') ||
            null
          )
        }
      },
      defaultVisibility(state: State): RequestVisibilityValue {
        return this.requestVisibility || state.account?.visibility_default
      },
      showRequester(state: State): boolean {
        return state.account?.show_requester || false
      }
    }),
    usStates(): { name: string; value: string }[] {
      const states: [string, string][] = this.states

      return states.map((state) => ({
        name: state[0],
        value: state[1]
      }))
    },
    isRequesterFieldsValid(): boolean {
      return Object.keys(this.isRequesterValid).every((key) => {
        if (key === 'email' && !this.requester[key]) {
          return true
        }
        return this.requester[key] && this.isRequesterValid[key]
      })
    },
    hasInvalidRequestFields(): boolean {
      return Object.values(this.isRequestFieldsValid).some(
        (isValid) => isValid === false
      )
    },

    disabledCreate(): boolean {
      if (!this.selectedDeptIds.length && this.departmentRequired) {
        return true
      }

      if (
        this.invisibleCaptcha ||
        this.isMakingRequest ||
        !this.requestText.trim() ||
        (this.termsAndConditionsRequired && !this.termsAndConditionsChecked) ||
        this.hasInvalidRequestFields
      ) {
        return true
      }

      if (!this.isRequesterFieldsDisabled && this.requesterRequired) {
        return !this.isRequesterFieldsValid
      }

      return false
    },

    noDeptsSelected(): boolean {
      return isEmpty(this.selectedDepartments)
    },

    selectedDeptIds(): Array<Number> {
      if (this.noDeptsSelected) return []

      if (this.multipleDeptSelection) {
        return this.selectedDepartments.map(
          (department: { name: string; id: number }) => department.id
        )
      }
      return [this.selectedDepartments.id]
    },
    formatedFormData(): object {
      const {
        email,
        name,
        phone: phone_number, // eslint-disable-line camelcase
        address,
        city,
        state,
        zip: zipcode,
        company
      } = this.requester

      const {
        invisibleCaptcha,
        dateReceived: request_date, // eslint-disable-line camelcase
        formatReceived: request_submit_type_id, // eslint-disable-line camelcase
        visibility,
        requestText: request_text, // eslint-disable-line camelcase
        selectedFolder,
        documentsToAdd: documents
      } = this

      if (invisibleCaptcha) {
        return
      }

      // eslint-disable-next-line camelcase
      const request_field_values = this.requestFields.map(
        (field: RequestField) => ({
          request_field_version_id: field.definition.version_id,
          value: field.value
        })
      )

      return {
        requester: {
          email,
          name,
          phone_number,
          address,
          city,
          state,
          zipcode,
          company
        },
        state: this.$can('read', 'staff_only_items') ? 'staff' : 'requester',
        documents,
        folder: selectedFolder?.folder_name,
        request: {
          request_text,
          request_date: dateFormat(new Date(request_date), 'MM/dd/yy'),
          request_submit_type_id,
          visibility: visibility === 'automatic' ? null : visibility,
          department_ids: this.selectedDeptIds,
          request_field_values
        }
      }
    },

    visibility: {
      get(): string {
        return this.requestVisibility || this.defaultVisibility
      },
      set(state: RequestVisibilityValue) {
        this.requestVisibility = state
      }
    },

    visibilityTips() {
      return [
        'Published: Everyone',
        ...(this.account.restricted_visibilities_feature_flag
          ? [
              'Published - department only: Admins, all assigned departments’ staff, requester, and any public user'
            ]
          : []),
        this.embargoVisibilityTip,
        'Automatic: The request visibility setting will be set by the portal and department default visibility settings.',
        'Staff: Staff + requester',
        "Department: Admins, assigned departments' staff + requester",
        'Restricted: Admins, assigned staff, + requester'
      ]
        .filter((tip) => !!tip)
        .join('<br /><br />')
    },

    visibilities(): RequestVisibility[] {
      return accountRequestVisibilities(this.account, true)
    },

    isSignedIn(): boolean {
      return !!this.currentUser
    },

    canManageDocuments(): boolean {
      return this.isSignedIn ? this.$can('read', 'staff_only_items') : false
    },

    s3Bucket(): string {
      return process.env.S3_BUCKET || ''
    },

    departmentSelectionText(): string {
      const isOptional = this.departmentRequired ? 'required' : 'optional'

      if (this.$can('read', 'staff_only_items')) {
        return `Assign departments (${isOptional})`
      } else {
        return `Select a department (${isOptional})`
      }
    },

    termsAndConditionsRequired(): boolean {
      if (this.$can('read', 'staff_only_items')) {
        return false
      } else {
        return !!this.termsAndConditions
      }
    },

    createRequestClassnames(): object {
      return {
        'is-loading': this.isMakingRequest
      }
    },

    multipleDeptSelection(): boolean {
      return (
        this.$can('read', 'staff_only_items') ||
        this.account.allow_multiple_department
      )
    }
  },

  methods: {
    handleCreateRequestByRole(): void {
      if (!this.requester.email && !this.no_email) {
        return this.toggleNoRequesterEmailModal(true)
      } else {
        return this.createRequest()
      }
    },
    handleRequestFieldValid(fieldId: number, isValid: boolean) {
      this.isRequestFieldsValid = {
        ...this.isRequestFieldsValid,
        [fieldId]: isValid
      }
    },
    handleRequesterInputValidState(field: string, isValid: boolean) {
      if (!field) {
        return
      }

      const validState =
        field === 'email' ? (this.emailDisabled ? true : isValid) : isValid

      this.isRequesterValid = Object.assign({}, this.isRequesterValid, {
        [`${field}`]: validState
      })
    },
    handlePerspectiveChange() {
      this.currentUserPerspective = this.selectedUserType
      this.getNewRequestData()
      this.togglePerspectiveModal(false)
    },
    validateEmailInput(input: string): boolean {
      return input.length ? EMAIL_REGEX.test(input) : true
    },
    createRequest(): void {
      this.toggleNoRequesterEmailModal(false)
      if (this.invisibleCaptcha) return

      this.isMakingRequest = true
      http
        // eslint-disable-next-line camelcase
        .post<
          NewRequest,
          { pretty_id?: string; email?: string; error?: string } // eslint-disable-line camelcase
        >('/requests', this.formatedFormData)
        .then((data) => {
          if (data.pretty_id) {
            // NOTE(TODO): We cannot let vue-router optimize loading and
            // rendering the requests how page until we stop loading the legacy
            // page. Or load the legacy request show with a different path
            // e.g., /requests/:id/legacy
            // This is because the RequestsController#show gets called and ends
            // up loading the legacy view for guest users.
            // this.$router.push({
            //   path: `/requests/${data.pretty_id}`
            // })
            window.location.href = `/requests/${data.pretty_id}?email=${data.email}`
          }
        })
        .catch((err) => {
          this.isMakingRequest = false
          const { email, error } = err.response.data
          if (email) {
            this.requester.email = email
          }
          const errorMessage =
            error || 'Request failed to save, please contact support'
          this.$toasted.error(errorMessage)
        })
    },

    handleEmailInput(): void {
      if (!this.requester.email) {
        this.isRequesterFieldsDisabled = false
        this.emailNotice = ''
      }

      if (this.requester.email === this.currentUser.email) {
        this.isRequesterFieldsDisabled = true
      } else {
        this.isRequesterFieldsDisabled = false
      }
    },

    handleEmailCheck(): void {
      if (!this.isSignedIn) {
        return
      }

      if (!this.$can('read', 'staff_only_items')) {
        return
      }

      if (!this.requester.email) {
        return
      }

      if (!EMAIL_REGEX.test(this.requester.email)) {
        return
      }

      http
        .get<AccountUser>(
          `/account_lookup?email=${encodeURIComponent(this.requester.email)}`
        )
        .then((accountUser) => {
          if (accountUser.email) {
            if (accountUser.email === this.currentUser.email) {
              // Make the current user the requester
              this.emailNotice =
                'Staff member: Are you sure? This will make YOU the requester.'
              this.isRequesterFieldsDisabled = true
            } else if (!accountUser.enabled) {
              // User account is deactived
              this.emailNotice =
                'This user account has been deactivated. It will need to be reactivated before the requester can sign in and receive emails'
            } else {
              this.emailNotice = 'Account found'
            }

            this.setRequesterInfo(accountUser)
          } else {
            this.emailNotice =
              'New email address. An account will be created for this requester.'
          }
        })
        .catch((error) => console.error(error))
    },

    setRequesterInfo(info: Requester) {
      this.requester = Object.assign({}, this.requester, info)
    },

    handleFolderSelection(names?: string[]): void {
      if (names && names.length) {
        this.selectedFolder = { id: -1, folder_name: names.pop() }
      } else {
        this.selectedFolder = null
      }
    },

    handleRequestInput(): void {
      if (this.requestText) {
        http
          .post('/alerts', { text: { search_text: this.requestText } })
          .then((data) => {
            this.keywordAlerts = data
          })
          .catch(() => {
            this.keywordAlerts = []
          })
      } else {
        this.keywordAlerts = []
      }
    },

    handleDocumentUploads(document: object) {
      this.uploadedDocuments.push(document)
    },

    handleDocumentRemoveClick(assetUrl: string): void {
      this.documentsToAdd = this.documentsToAdd.filter(
        (document: Document) => document.url !== assetUrl
      )
      this.$toasted.success('Successfully removed document')
    },

    handleUploadModalClick(): void {
      this.toggleUploadModal()
    },

    toggleNoRequesterEmailModal(state?: boolean): void {
      if (state === undefined || state === null) {
        this.showNoRequesterEmailModal = !this.showNoRequesterEmailModal
      } else {
        this.showNoRequesterEmailModal = state
      }
    },
    togglePerspectiveModal(state: boolean): void {
      if (!state) {
        this.selectedUserType = this.currentUserPerspective
      }
      this.showPerspectiveModal = state
    },
    toggleUploadModal(state?: boolean): void {
      if (state === undefined || state === null) {
        this.showUploadModal = !this.showUploadModal
      } else {
        this.showUploadModal = state
      }
      if (!this.showUploadModal) this.uploadedDocuments = []
    },

    getNewRequestData(): void {
      http
        .get<NewRequestPrep>('/requests/new', {
          custom_fields_preview: this.customFieldsPreview,
          preview_perspective: this.currentUserPerspective
        })
        .then((data) => {
          this.states = (data.states || []).filter(
            (state: [string, string]) => state && state[0] && state[1]
          )
          this.formats = data.formats
          this.embargoVisibilityTip = data.embargo_visibility_tooltip

          const initialRequestFieldValid = {}
          const parsedRequestFields: RequestField[] = []
          const preParsedRequestFields = data.request_fields || []

          preParsedRequestFields.forEach((definition) => {
            if (definition.type_settings?.isMandatory) {
              initialRequestFieldValid[definition.id] = false
            }

            parsedRequestFields.push({ definition, value: null })
          })

          this.isRequestFieldsValid = initialRequestFieldValid
          this.requestFields = parsedRequestFields

          this.requestVisibility = 'automatic'

          if (this.isSignedIn && !this.$can('read', 'staff_only_items')) {
            this.requester.email = this.currentUser.email
            if (this.currentUser.email !== this.currentUser.email_or_name) {
              this.requester.name = this.currentUser.email_or_name
            }
          }
        })
    },
    handleSaveDocumentsToRequest(data: {
      [key: string]: any
      documents: []
    }): void {
      this.documentsToAdd = [...this.documentsToAdd, ...data.documents]
      this.$toasted.success('Successfully uploaded document(s)')
    },

    handleDepartmentSelectionEvent(eventType: string, data?: any) {
      if (eventType === 'typing') {
        this.departmentSearchTerm = (data || '').trim()
      } else {
        this.departmentSearchTerm = ''
      }
    },

    parseQueryParams(): void {
      if (isEmpty(this.$route.query)) return

      const { dept_id: deptId } = this.$route.query
      if (deptId) {
        const dept = this.departments.find(
          (dept: { id: number; name: string }) => +dept.id === +deptId
        )
        if (!dept) return
        if (this.multipleDeptSelection) {
          this.selectedDepartments = [dept]
        } else {
          this.selectedDepartments = dept
        }
      }
    },

    formatDate(date) {
      return dateFormat(new Date(date), 'MM/dd/yyyy')
    },

    addAccessibilityAlert(formatting: string, on: boolean) {
      const action = on ? 'enabled' : 'disabled'
      this.accessibilityAlerts.push(formatting + ' formatting ' + action)
    }
  },

  watch: {
    customFieldsPreview() {
      this.getNewRequestData()
    },
    departments(next) {
      if (!next) return
      if (!next.length) return

      this.parseQueryParams()
    },

    currentUser(next, _prev) {
      if (!next) {
        return
      }
      if (!next.email) {
        return
      }
      if (this.$can('read', 'staff_only_items')) {
        return
      }

      this.requester.email = next.email
    },
    emailDisabled(disabled) {
      if (disabled) {
        this.isRequesterFieldsDisabled = false
      }
      if (this.requester.email) {
        this.requester.email = ''
      }
    }
  }
})
